import React, {useEffect, useRef, useState} from 'react';
import * as d3 from 'd3';
import {dfsSequence, HierarchyNode as HNode, HierarchyRoot} from '../../util/EmployeeHierarchy';
import {Box, Theme, useTheme} from '@mui/material';
import {CompanyTreemapFilterForm, toCompanyTreemapFilter} from "./CompanyTreemapFilter";
import {numberCmp} from "../../util/SortUtil";
import {matchesFilter} from "../../util/FilterUtil";

function bgColor(node: HNode, theme: Theme) {
    return node.neighbourhoods[0]?.displayColor || theme.palette.grey["100"]
}

function fgColor(node: HNode, theme: Theme) {
    return theme.palette.getContrastText(bgColor(node, theme));
}

function calculateHeight(root: HierarchyRoot, width: number): number {
    let totalNodes = 0;
    for (const node of dfsSequence(root)) {
        void node;
        totalNodes++;
    }

    // Minimum area per node (in square pixels)
    // Adjust this value based on readability requirements
    const minAreaPerNode = 10000;

    const totalArea = totalNodes * minAreaPerNode;
    return Math.max(400, Math.ceil(totalArea / width));
}


export function CompanyTreemap({
    hierarchyData,
    filter,
}: {
    hierarchyData: HierarchyRoot,
    filter: CompanyTreemapFilterForm,
}) {
    const containerRef = useRef<HTMLDivElement>(null);
    const svgRef = useRef<SVGSVGElement>(null);
    const [width, setWidth] = useState<number>();
    const theme = useTheme();

    useEffect(() => {
        const containerElement = containerRef.current;
        if (!containerElement) return;
        const resizeObserver = new ResizeObserver(() => {
            setWidth(containerElement.clientWidth);
        });
        resizeObserver.observe(containerElement);
        return () => {
            resizeObserver.disconnect();
        };
    }, []);

    useEffect(() => {
        const svgElement = svgRef.current;
        if (!hierarchyData || hierarchyData.children.length === 0 || !svgElement || !width) return;

        // Clear previous visualization
        d3.select(svgElement).selectAll('*').remove();

        const filterFn = toCompanyTreemapFilter(filter);

        const hierarchy = d3.hierarchy<HNode>(
            hierarchyData,
            node => node.children,
        )
            .sum(() => 1)
            .sort(numberCmp('height', true));

        const height = calculateHeight(hierarchyData, width);

        const treemap = d3.treemap<HNode>()
            .size([width, height])
            .paddingOuter(3)
            .paddingTop(node => node.data.neighbourhoods ? 29 : 19)
            .paddingInner(1)
            .round(true);

        const nodes = treemap(hierarchy);

        const svg = d3.select(svgElement)
            .attr('width', width)
            .attr('height', height)
            .style('font-family', 'sans-serif')
            .style('overflow', 'visible');

        const cell = svg.selectAll('g')
            .data(nodes.descendants())
            .join('g')
            .attr('transform', d => `translate(${d.x0},${d.y0})`);

        // Cell rectangle
        cell.append('rect')
            .attr('width', d => d.x1 - d.x0)
            .attr('height', d => d.y1 - d.y0)
            .attr('fill', d => bgColor(d.data, theme))
            .attr('stroke', d => fgColor(d.data, theme))
            .attr('opacity', d => matchesFilter(d.data, filterFn) ? 1 : 0.3);

        // Clip path for
        cell.append('clipPath')
            .attr('id', (_, i) => `clip-${i}`)
            .append('rect')
            .attr('width', d => d.x1 - d.x0 - 6)
            .attr('height', d => d.y1 - d.y0)
            .attr('x', 3);

        // Cell title
        cell.append('text')
            .attr('x', 3)
            .attr('y', 13)
            .attr('fill', d => fgColor(d.data, theme))
            .text(d => d.data.fullName)
            .style('font-size', '12px')
            .style('pointer-events', 'none')
            .attr('clip-path', (_, i) => `url(#clip-${i})`)
            .attr('opacity', d => matchesFilter(d.data, filterFn) ? 1 : 0.3);

        // Cell subtitle
        cell.append('text')
            .attr('x', 3)
            .attr('y', 25)
            .attr('fill', d => fgColor(d.data, theme))
            .text(d => d.data.neighbourhoods[0]?.name || '')
            .style('font-size', '10px')
            .style('pointer-events', 'none')
            .attr('clip-path', (_, i) => `url(#clip-${i})`)
            .attr('opacity', d => matchesFilter(d.data, filterFn) ? 1 : 0.3);

        cell.append('title')
            .text(d => {
                if (d.data.type === 'node') {
                    const employee = d.data;
                    return [
                        {label: 'Name', value: employee.fullName},
                        {label: 'Residence', value: `${employee.residence.workSite.badge || employee.residence.workSite.name} / ${employee.residence.hibobOffice ? employee.residence.hibobOffice.badge || employee.residence.hibobOffice.name : 'no office'}`},
                        {label: 'Neighbourhood', value: employee.neighbourhoods.map(n => n.name).join(', ')},
                        {label: 'Team', value: employee.teamPath?.map(t => t.name).join(' > ')},
                    ]
                        .filter(it => it.value)
                        .map(it => `${it.label}: ${it.value}`)
                        .join('\n');
                } else {
                    return d.name;
                }
            });

    }, [hierarchyData, theme, width, filter]);

    return (
        <Box ref={containerRef} component="div" width="100%">
            <svg ref={svgRef}/>
        </Box>
    );
}
